// Retrying a request that failed for a reason that will not repeat.
//
// It exists for the app's BOOT reads. Everywhere else a failed fetch lands in a store whose screen
// already shows the error next to a reload the user can press, so one attempt is enough. The boot
// reads have no such fallback: they run before there is a page, so the whole dashboard turns into a
// dead end for the length of a blip that would have cleared on its own. `api` runs two instances
// behind the edge and deploys roll one at a time, so a request in flight across the handover can be
// answered by the edge (502/503/504) rather than by us — the deploy window is the one moment every
// user is most likely to reload into.
//
// Deliberately NOT put in the transport: retrying every call would double-send the writes as well.
// The caller names the calls that are safe to repeat.

// Only failures that another identical request could plausibly survive. A 4xx we caused, or a 500
// out of our own code, repeats exactly — retrying it just delays the message by a second.
//
// A failure with no `status` never reached a response: fetch rejects that way for a dropped
// connection, a DNS blip or a CORS preflight the edge answered while the instance behind it was
// leaving. Auth0's failures arrive through the same call and carry no status either, but they are
// answered elsewhere — getToken() sends the user back through login on the ones that need it — and
// repeating the request would repeat that redirect, so an Auth0-shaped error (a string `error`
// code, which none of ours has) is excluded.
const RETRYABLE_STATUSES = [408, 425, 429, 502, 503, 504];

export function isTransientFailure(error) {
  if (typeof error?.error === "string") return false;
  if (error?.status === undefined) return true;
  return RETRYABLE_STATUSES.includes(error.status);
}

// Waits between attempts rather than re-asking instantly: the failures worth retrying are the ones
// where something upstream is mid-change, and the length of the pause is what gives it time to
// finish. Kept short all the same — the user is watching a loader for the whole of it.
export const DEFAULT_DELAYS_MS = [400, 1200];

// `delaysMs[i]` is the pause before attempt i+2, so the number of attempts is its length plus one.
export async function withRetry(attempt, { delaysMs = DEFAULT_DELAYS_MS, shouldRetry = isTransientFailure } = {}) {
  for (let index = 0; ; index += 1) {
    try {
      return await attempt();
    } catch (error) {
      if (index >= delaysMs.length || !shouldRetry(error)) throw error;
      await new Promise((resolve) => setTimeout(resolve, delaysMs[index]));
    }
  }
}
